import {AlertCircleIcon, ArrowUpIcon, ArrowDownIcon, TrendingUpIcon, UsersIcon} from "lucide-react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {useVNextRevenue} from "@hooks/use-vnext";

import {ExecutiveSummary} from "./ExecutiveSummary";

interface RevenueTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

function fmt(n: number): string {
    return `$${n.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
}

function currentMonthKey(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${now.getFullYear()}-${month}`;
}

function cleanCustomerName(name?: string | null): string {
    if (!name) return "Unknown";
    const cleaned = name.replace(/\\+$/g, "").replace(/\s+/g, " ").trim();
    return cleaned || "Unknown";
}

export function RevenueTab({workspaceId, scenarioId, actualMonths = 12, period, selectedMonth, customFrom, customTo, fyStartMonth, currentMonth}: RevenueTabProps) {
    const {data, isLoading, error} = useVNextRevenue(workspaceId, actualMonths, {
        period,
        selectedMonth,
        customFrom,
        customTo,
        fyStartMonth,
    });

    if (isLoading) {
        return (
            <div className="grid gap-4 md:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <Card key={i}>
                        <CardHeader className="pb-2">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-8 w-32 mt-1" />
                        </CardHeader>
                    </Card>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">
                    <AlertCircleIcon className="size-5 inline mr-2" />
                    {error}
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    const top_customers = data.top_customers ?? [];
    const monthly_trend = data.monthly_trend ?? [];
    const new_vs_repeat = data.new_vs_repeat ?? null;
    const kpis = data.kpis ?? {total_revenue: 0, avg_monthly_revenue: 0, mom_growth_pct: null, customer_count: 0};
    const activeCurrentMonth = currentMonth || currentMonthKey();

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto space-y-6">
            <ExecutiveSummary workspaceId={workspaceId} scenarioId={scenarioId} tabKey="revenue" period={period} selectedMonth={selectedMonth} customFrom={customFrom} customTo={customTo} fyStartMonth={fyStartMonth} currentMonth={currentMonth} />

            {/* KPI Strip */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Total Revenue</CardDescription>
                        <CardTitle className="text-2xl tabular-nums">{fmt(kpis.total_revenue)}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-xs text-muted-foreground">Last {data.months} months</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Avg Monthly Revenue</CardDescription>
                        <CardTitle className="text-2xl tabular-nums">{fmt(kpis.avg_monthly_revenue)}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>MoM Growth</CardDescription>
                        <CardTitle className="text-2xl tabular-nums flex items-center gap-1">
                            {kpis.mom_growth_pct !== null ? (
                                <>
                                    {kpis.mom_growth_pct >= 0 ? (
                                        <ArrowUpIcon className="size-5 text-green-600" />
                                    ) : (
                                        <ArrowDownIcon className="size-5 text-red-600" />
                                    )}
                                    <span className={kpis.mom_growth_pct >= 0 ? "text-green-600" : "text-red-600"}>
                                        {kpis.mom_growth_pct}%
                                    </span>
                                </>
                            ) : (
                                "—"
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-xs text-muted-foreground">vs previous month</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>Active Customers</CardDescription>
                        <CardTitle className="text-2xl tabular-nums">{kpis.customer_count}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-xs text-muted-foreground">With revenue in period</p>
                    </CardContent>
                </Card>
            </div>

            {/* Top Customers */}
            {top_customers.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <UsersIcon className="size-5 text-[#209DB3]" />
                            <CardTitle className="text-base">Top Customers by Revenue</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>#</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead className="text-right">Revenue</TableHead>
                                    <TableHead className="text-right">Invoices</TableHead>
                                    <TableHead className="text-right">% of Total</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {top_customers.slice(0, 15).map((c, i) => (
                                    <TableRow key={c.contact_id}>
                                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                                        <TableCell className="font-medium">{cleanCustomerName(c.contact_name)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmt(c.total_revenue)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{c.txn_count}</TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {kpis?.total_revenue != null && kpis.total_revenue > 0
                                                ? ((c.total_revenue / kpis.total_revenue) * 100).toFixed(1)
                                                : "0"}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Monthly Trend — periods across, metrics in rows */}
            {monthly_trend.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                            <TrendingUpIcon className="size-5 text-[#209DB3]" />
                            <CardTitle className="text-base">Monthly Revenue Trend</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <Table className="w-auto" style={{minWidth: `max(100%, ${140 + monthly_trend.length * 100}px)`}}>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="sticky left-0 z-10 bg-background min-w-[140px]">Metric</TableHead>
                                    {monthly_trend.map((m) => (
                                        <TableHead key={m.month_key} className="text-right min-w-[100px]">
                                            {m.month_key}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow>
                                    <TableCell className="sticky left-0 z-10 bg-background font-medium">Revenue</TableCell>
                                    {monthly_trend.map((m) => (
                                        <TableCell key={m.month_key} className="text-right tabular-nums">
                                            {fmt(m.total_revenue)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell className="sticky left-0 z-10 bg-background font-medium">MoM Change</TableCell>
                                    {monthly_trend.map((m, i) => {
                                        const isIncompleteCurrentMonth = m.month_key === activeCurrentMonth;
                                        const prev = i > 0 ? monthly_trend[i - 1].total_revenue : null;
                                        const change = !isIncompleteCurrentMonth && prev && prev !== 0
                                            ? ((m.total_revenue - prev) / Math.abs(prev)) * 100
                                            : null;
                                        return (
                                            <TableCell key={m.month_key} className="text-right tabular-nums">
                                                {isIncompleteCurrentMonth ? (
                                                    <span className="text-muted-foreground">N/A</span>
                                                ) : change !== null ? (
                                                    <span className={change >= 0 ? "text-green-600" : "text-red-600"}>
                                                        {change >= 0 ? "+" : ""}{change.toFixed(1)}%
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* New vs Repeat */}
            {new_vs_repeat && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">New vs Repeat Customers</CardTitle>
                        <CardDescription>For {new_vs_repeat.month}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex items-center justify-between p-3 rounded-lg border">
                                <div>
                                    <p className="text-sm font-medium">New Customers</p>
                                    <p className="text-2xl font-bold tabular-nums">{new_vs_repeat.new_count}</p>
                                </div>
                                <div className="text-right">
                                    <Badge variant="outline" className="text-xs">
                                        {fmt(new_vs_repeat.new_revenue)}
                                    </Badge>
                                </div>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg border">
                                <div>
                                    <p className="text-sm font-medium">Repeat Customers</p>
                                    <p className="text-2xl font-bold tabular-nums">{new_vs_repeat.repeat_count}</p>
                                </div>
                                <div className="text-right">
                                    <Badge variant="outline" className="text-xs">
                                        {fmt(new_vs_repeat.repeat_revenue)}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
